import { useState, useEffect, useLayoutEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
// routes
import { useSnackbar } from 'notistack';
import { IconButton, Button, Typography, Box } from '@mui/material';
import { Close as IconClose } from '@mui/icons-material';
import Router from './routes';
// theme
import ThemeConfig from './theme';
// hooks
import useAuth from './hooks/useAuth';
// components
import RtlLayout from './components/RtlLayout';
import ScrollToTop from './components/ScrollToTop';
import LoadingScreen from './components/LoadingScreen';
// import GoogleAnalytics from './components/GoogleAnalytics';
import NotistackProvider from './components/NotistackProvider';
import ThemePrimaryColor from './components/ThemePrimaryColor';
// material
import { config } from './_config';
import { PublicClientApplication } from '@azure/msal-browser'
import { api } from './service';
import { setAuthToken } from './utils/axios';
import ReactGA from 'react-ga';
import detectZoom from 'detect-zoom';
import DeviceOrientation, { Orientation } from 'react-screen-orientation'

// ----------------------------------------------------------------------


const TRACKING_ID = "UA-120137924-1"; // OUR_TRACKING_ID
ReactGA.initialize(TRACKING_ID);



const pageViewsTracking = (props) => {
  const pathname = props.match.path

  let pageView;
  if(pathname === "*")
    pageView = '/not-found'
  else
    pageView = pathname
  
  ReactGA.pageview(pageView)
}


function SnackbarCloseButton({ snackbarKey }) {
  const { closeSnackbar } = useSnackbar();

  return (
    <IconButton onClick={() => closeSnackbar(snackbarKey)}>
      <IconClose />
    </IconButton>
  );
}

// localStorage.setItem coerces undefined/null to the literal strings
// "undefined"/"null", which are indistinguishable from a real value everywhere
// downstream. Remove the key instead, so a missing value reads as missing.
const setOrRemoveItem = (key: string, value: any) => {
  if (value === undefined || value === null || value === '') {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, String(value));
  }
};

// The Azure AD `department` string is matched against the business line list by
// name. This used to be a strict ===, so a difference in casing, padding or
// repeated whitespace produced no match at all -- and the caller then stored
// `undefined` as the business line id.
const findBusinessLineByName = (businessLines: any[], department: any) => {
  const normalize = (value: any) =>
    String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

  const wanted = normalize(department);
  if (!wanted) return undefined;

  return (businessLines || []).find((b: any) => normalize(b?.name) === wanted);
};

const MainAppContent = () => {
  const { pathname, search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const isNonEmbedVideo = urlParams.get('is_non_embed');
  const isVideoRoute = pathname.includes('/video');
  // console.log('isNonEmbedVideo', isNonEmbedVideo)
  // console.log('isVideoRoute', isVideoRoute)
  // const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [_role, setRole] = useState(null)
  const [isMounted, setIsMounted] = useState(false);
  const [orientation, setOrientation] = useState(window.orientation || 0);

  useEffect(() => {
    const handler = () => setOrientation(window.orientation || 0);
    window.addEventListener('orientationchange', handler);
    return () => window.removeEventListener('orientationchange', handler);
  }, []);

  // console.log(pathname);
  // if (pathname[pathname.length - 1] === '/' && pathname.length !== 1)
  //   window.location.href = pathname.slice(0, -1);

  const navigate = useNavigate();
  useEffect(() => {
    if (pathname.endsWith('/') && pathname !== '/') {
      navigate(pathname.slice(0, -1) + search, { replace: true });
    }
  }, [pathname, search, navigate]);

  useEffect(() => {
    const handleRoleChanged = () => {
      setRole(localStorage.getItem('role'));
    };

    const handleStorageRoleChanged = (event: StorageEvent) => {
      if (!event.key || event.key === 'role') {
        handleRoleChanged();
      }
    };

    window.addEventListener('role-changed', handleRoleChanged);
    window.addEventListener('storage', handleStorageRoleChanged);
    return () => {
      window.removeEventListener('role-changed', handleRoleChanged);
      window.removeEventListener('storage', handleStorageRoleChanged);
    };
  }, []);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false
      }
    }
  });

  const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return { height, width }
  };

  useLayoutEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isAuth) return;
  
    const checkRoleInterval = setInterval(async () => {
      try {
        const userSid = localStorage.getItem('sid');
        const email = localStorage.getItem('email');
        if (!userSid && !email) return;
        const data = await api.getRole(userSid ? { user_sid: userSid } : { mail: email });
        if (data && data[0] && data[0].role) {
          const currentRole = data[0].role;
          const storedRole = localStorage.getItem('role');
          if (currentRole !== storedRole) {
            localStorage.setItem('role', currentRole);
            setRole(currentRole);
            window.dispatchEvent(new Event('role-changed'));
            navigate('/', {
              replace: true,
              state: {
                roleChangedAt: Date.now(),
                role: currentRole
              }
            });
          }
        }
      } catch (error) {
        console.error('Error checking role:', error);
      }
    }, 5000);
  
    return () => clearInterval(checkRoleInterval);
  }, [isAuth, navigate]);


  const handleZoom = () => {
    const { width, height } = getWindowDimensions()
    if(width < 1300 && width > 500) {
      document.body.style.zoom = '80%'
      // document.body.style.scale = 'scale(0.8)'
      // var scale = 'scale(0.9)';
      // document.body.style.webkitTransform =  scale;    // Chrome, Opera, Safari
      //  document.body.style.msTransform =   scale;       // IE 9
      //  document.body.style.transform = scale;
      // document.body.style.scale = 'scale(0.8)'
    }
    else {
      // var scale = '';
      document.body.style.zoom = '100%'
      // document.body.style.webkitTransform =  scale;    // Chrome, Opera, Safari
      // document.body.style.msTransform =   scale;       // IE 9
      // document.body.style.transform = scale;

    }

    var browserZoomLevel = Math.round(window.devicePixelRatio * 100);
    console.log(browserZoomLevel)
    console.log(detectZoom.device())
    // if(browserZoomLevel === 300) {
    //   document.body.style.zoom = '80%'
    //   console.log(document.body.style.zoom)
    // }
    // else if(browserZoomLevel > 200) {
    //   const l = browserZoomLevel / 200
    //   console.log(`${1/l*100}%`)
    //   document.body.style.zoom = `${1/l*100}%`
    //   console.log(document.body.style.zoom)
    // }
    // if (detectZoom.device() * 100 !== 100) {
    //   console.log('your zoom is not 100')
    // } else {
    //   console.log('your zoom is 100')
    // }   
  }  

  // useEffect(() => {
  //   const initialValue = document.body.style.zoom;
  //   var browserZoomLevel = Math.round(window.devicePixelRatio * 100);
  //   if(browserZoomLevel === 300) {
  //     document.body.style.zoom = '80%'
  //     console.log(document.body.style.zoom)
  //   }
  //   else {
  //     const l = browserZoomLevel / 200
  //     document.body.style.zoom = `${1/l*100}%`
  //   }

  //   // Change zoom level on mount
  //   // document.body.style.zoom = "150%";

  //   return () => {
  //     // Restore default value
  //     document.body.style.zoom = initialValue;
  //   };
  // }, []);

  useEffect(async () => {
    handleZoom();
    window.addEventListener('resize', handleZoom);
    const isAuthenticated = localStorage.getItem('isAuthenticated')
    // console.log(isAuthenticated)
    setIsAuth(isAuthenticated == 'true')
    if(!isAuthenticated || isAuthenticated == 'false')
      await login()
  }, [])

  const publicClientApplication = new PublicClientApplication({
    auth: {
      clientId: config.appId,
      redirectUri: config.redirectUri,
      authority: config.authority
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false
    }
  })

  const login = async () => {
    let accountId = ""
    try {
      // await publicClientApplication.loginPopup({
      //   scopes: config.scopes,
      //   prompt: 'select_account'
      // })
      // console.log("1")

      // const accountId = 'ba5f2a93-291b-40ac-958e-d0fa3a064a44'
      // localStorage.setItem('businessLine', 'Audit & Assurance')
      // localStorage.setItem('businessLineId', 'c6dc6f08-f196-43fe-9eeb-90cbe6260589')
      // localStorage.setItem('country', 'Croatia')
      // localStorage.setItem('role', 'user')
      // setRole('user')
      // localStorage.setItem('sid', accountId)
      // localStorage.setItem('name', 'Katarina Kadunc')
      // localStorage.setItem('email', 'kkadunc@deloittece.com')
      // localStorage.setItem('isAuthenticated', 'true')
      // setIsAuth(true)

      // const accountId = '18a249f0-cb4d-4aba-ab41-fb8e4a3f69d4'
      // localStorage.setItem('businessLine', 'Consulting')
      // localStorage.setItem('businessLineId', '61cd298f-bde9-4aba-9680-f05e0651d2d7')
      // localStorage.setItem('country', 'Czech Republic')
      // localStorage.setItem('role', 'lead_admin')
      // setRole('user')
      // localStorage.setItem('sid', accountId)
      // localStorage.setItem('name', 'Dmitriy Kostenko')
      // localStorage.setItem('email', 'dmkostenko@deloittece.com')
      // localStorage.setItem('isAuthenticated', 'true')
      // setIsAuth(true)

      // const accountId = '460adf48-cfae-432a-a078-36ec37caf9bd'
      // localStorage.setItem('businessLine', 'Tax & Legal')
      // localStorage.setItem('businessLineId', '6f7facb2-ac59-4372-ae08-3328845a4ebd')
      // localStorage.setItem('country', 'Hungary')
      // localStorage.setItem('role', 'user')
      // setRole('user')
      // localStorage.setItem('sid', accountId)
      // localStorage.setItem('name', 'TestTaL HU')
      // localStorage.setItem('email', 'testtalhu@deloittece.com')
      // localStorage.setItem('isAuthenticated', 'true')
      // setIsAuth(true)

      publicClientApplication.handleRedirectPromise()
        .then(async (response) => {
          console.log("2")
          console.log(response)
          let homeAccountId = ""
          let name = ""
          let email = ""  
          if (response !== null) {
            console.log("3")
              accountId = response.account.localAccountId;
              homeAccountId = response.account?.homeAccountId;
              name = response.account.name;
              email = response.account.username;
              // Display signed-in user content, call API, etc.
              // localStorage.setItem('isAuthenticated', 'true')
              // setIsAuth(true)
              localStorage.setItem('token', response.idToken)
              setAuthToken(response.idToken)
          } else {
            console.log("4")
              // In case multiple accounts exist, you can select
              const currentAccounts = publicClientApplication.getAllAccounts();
              console.log("5")

              if (currentAccounts.length === 0) {
                  // no accounts signed-in, attempt to sign a user in
                  publicClientApplication.loginRedirect({
                    scopes: config.scopes
                  });
                  console.log("6")

              } else if (currentAccounts.length > 1) {
                  // Add choose account code here
              } else if (currentAccounts.length === 1) {
                  accountId = currentAccounts[0].localAccountId;
                  homeAccountId = currentAccounts[0].homeAccountId;
                  name = currentAccounts[0].name;
                  email = currentAccounts[0].username;
              }
          }
          if(name && email) {
            console.log("7")
            // accountId = '315a8d3c-3a62-4eda-8fe6-82f0106611aa'
            const businessLines = await api.getBusinessLines({
              page_number: 1,
              items_count: 10000
            })
            console.log("8")
            // localStorage.setItem('businessLine', 'Audit & Assurance')
            // localStorage.setItem('businessLineId', '61cd298f-bde9-4aba-9680-f05e0651d2d9')
            // localStorage.setItem('role', 'user')
            // setRole('user')
            // localStorage.setItem('sid', accountId)
            // localStorage.setItem('name', name)
            // localStorage.setItem('email', email)
            // localStorage.setItem('isAuthenticated', 'true')
            // setIsAuth(true)
            api.getRole({
              mail: email
              // mail: 'rstulik@deloittece.com'
            }).then(async (data) => {
              console.log("9")
              if(data && data[0] && data[0]?.role && data[0]?.original_role) {
                console.log("10")
                console.log(data)
                const role = data[0]?.role
                const originalRole = data[0]?.original_role
                const department = data[0]?.user_department
                const country = data[0]?.user_country
                localStorage.setItem('country', country)
                // localStorage.setItem('country', 'Czech Republic')
                const matchedBusinessLine = findBusinessLineByName(businessLines, department)
                if (department && !matchedBusinessLine) {
                  console.error('[dTube] no business line matches AAD department:', department)
                }
                setOrRemoveItem('businessLineId', matchedBusinessLine?.id)
                if(role === 'user') {
                  setOrRemoveItem('businessLine', department)
                }
                else if(role === 'approver') {
                  localStorage.setItem('businessLine', 'Approver')
                  // localStorage.setItem('businessLineId', '61cd298f-bde9-4aba-9680-f05e0651d2d9')
                }
                else if(role === 'lead_admin') {
                  localStorage.setItem('businessLine', 'Lead Admin')
                  // localStorage.setItem('businessLineId', '61cd298f-bde9-4aba-9680-f05e0651d2d9')
                }
                else if(role === 'admin_coordinator') {
                  localStorage.setItem('businessLine', 'Admin Coordinator')
                  // localStorage.setItem('businessLineId', '61cd298f-bde9-4aba-9680-f05e0651d2d9')
                }
                else if(role === 'livestream_admin') {
                  localStorage.setItem('businessLine', 'Live Stream Admin')
                  // localStorage.setItem('businessLineId', '61cd298f-bde9-4aba-9680-f05e0651d2d9')
                }
                else if(role === 'data_user') {
                  localStorage.setItem('businessLine', 'Data Admin')
                  // localStorage.setItem('businessLineId', '61cd298f-bde9-4aba-9680-f05e0651d2d9')
                }
                else {
                  localStorage.setItem('businessLine', 'Lead Admin')
                  // localStorage.setItem('businessLineId', '61cd298f-bde9-4aba-9680-f05e0651d2d9')
                }
                localStorage.setItem('role', role)
                localStorage.setItem('originalRole', originalRole)
                setRole(role)
                localStorage.setItem('sid', accountId)
                localStorage.setItem('homeAccountId', homeAccountId)
                localStorage.setItem('name', name)
                localStorage.setItem('email', email)
                localStorage.setItem('isAuthenticated', 'true')
                setIsAuth(true)
                // const logoutRequest = {
                //   account: publicClientApplication.getAccountByHomeId(accountId)
                // }            
                // await publicClientApplication.logoutRedirect(logoutRequest);
              }
              else {
                api.setRole({
                  user_sid: accountId,
                  mail: email,
                  role: 'user'
                }).then(() => {
                  localStorage.setItem('country', data?.[0]?.user_country)
                  const firstLoginDepartment = data?.[0]?.user_department
                  const firstLoginBusinessLine = findBusinessLineByName(businessLines, firstLoginDepartment)
                  if (firstLoginDepartment && !firstLoginBusinessLine) {
                    console.error('[dTube] no business line matches AAD department:', firstLoginDepartment)
                  }
                  setOrRemoveItem('businessLine', firstLoginDepartment)
                  setOrRemoveItem('businessLineId', firstLoginBusinessLine?.id)
                  localStorage.setItem('role', 'user')
                  setRole('user')
                  localStorage.setItem('sid', accountId)
                  localStorage.setItem('homeAccountId', homeAccountId)
                  localStorage.setItem('name', name)
                  localStorage.setItem('email', email)
                  localStorage.setItem('isAuthenticated', 'true')
                  setIsAuth(true)
                })
              }
            })
            .catch(async (err) => {
              console.log("11")
              console.log(err)
              const logoutRequest = {
                account: publicClientApplication.getAccountByHomeId(accountId)
              }
              localStorage.setItem('isAuthenticated', 'false')
              setIsAuth(false)
              localStorage.clear()
              await publicClientApplication.logoutRedirect(logoutRequest);
              // window.location.reload()
            })
          }
        })
        .catch(async (err) => {
          console.log("12")
          console.log(err)
          const logoutRequest = {
            account: publicClientApplication.getAccountByHomeId(accountId)
          }
          localStorage.setItem('isAuthenticated', 'false')
          setIsAuth(false)
          localStorage.clear()
          await publicClientApplication.logoutRedirect(logoutRequest);
          // window.location.reload()
        });
    }
    catch(err) {
      // console.log("13")
      // console.log(err)
      const logoutRequest = {
        account: publicClientApplication.getAccountByHomeId(accountId)
      }
      localStorage.setItem('isAuthenticated', 'false')
      setIsAuth(false)
      localStorage.clear()
      await publicClientApplication.logoutRedirect(logoutRequest);
      window.location.reload()
    }
  }
  
  // Authentication & queryClient logic…
  // Render your routes (or a QueryClientProvider wrapping your Router)
  return (isVideoRoute && isNonEmbedVideo)
    ? (isAuth && (
        <QueryClientProvider client={queryClient}>
          <ScrollToTop />
          <Router role={_role} />
        </QueryClientProvider>
      ))
    : (isAuth && (
        <QueryClientProvider client={queryClient}>
          <ScrollToTop />
          <Router role={_role} />
        </QueryClientProvider>
      ));
}

export default function App() {

  // const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  // const isTablet = /iPad|Android/.test(navigator.userAgent) && (screen.width >= 990 && screen.width <= 1366);
  // console.log('navigator.userAgent', navigator.userAgent)
  // console.log('screen.width', screen.width)
  // console.log('isTablet', isTablet)
  // console.log('isMobile', isMobile)
  // const isMobileOrTablet = isMobile || isTablet;  
  // const isVideoPath = window.location.pathname.includes('/video');

  // iPadOS often reports as "Mac" but has touch
  const isIPadOSDesktopUA =
    typeof navigator !== 'undefined' &&
    navigator.platform === 'MacIntel' &&
    (navigator as any).maxTouchPoints > 1;

  const hasTouch =
    typeof navigator !== 'undefined' &&
    ((navigator as any).maxTouchPoints > 0 || 'ontouchstart' in window);

  // Use CSS pixels, not screen.width
  const w = typeof window !== 'undefined' ? window.innerWidth : 1920;

  // Your breakpoints – tweak if your design uses others
  const isTablet = hasTouch && w >= 768 && w <= 1366;
  const isMobile = hasTouch && w < 768;

  // Treat iPadOS desktop UA as tablet
  const isMobileOrTablet = isMobile || isTablet || isIPadOSDesktopUA;

  // Orientation via media query
  const isLandscape =
    typeof window !== 'undefined' &&
    window.matchMedia('(orientation: landscape)').matches;

  const { pathname } = useLocation();

  const isVideoPath = pathname.includes('/video');

  const [, forceRerender] = useState(0);
  useEffect(() => {
    const onResize = () => forceRerender(x => x + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  function useIsMobileWidescreen() {
      const get = () => {
        const w = window.innerWidth || 0;
        const h = window.innerHeight || 0;
        const isLandscape = w > h;
        // "phone-ish" landscape: short height, not too huge width
        const isPhoneLike = h <= 520 && w <= 1024;
        return isLandscape && isPhoneLike;
      };
  
      const [val, setVal] = useState(get);
  
      useEffect(() => {
        const onChange = () => setVal(get());
        window.addEventListener('resize', onChange);
        window.addEventListener('orientationchange', onChange);
        onChange();
        return () => {
          window.removeEventListener('resize', onChange);
          window.removeEventListener('orientationchange', onChange);
        };
      }, []);
  
      return val;
  }

  const isMobileWidescreen = useIsMobileWidescreen();



  return (
    <ThemeConfig>
      <ThemePrimaryColor>
        <RtlLayout>
          <NotistackProvider autoHideDuration={2000} action={snackbarKey => <SnackbarCloseButton snackbarKey={snackbarKey} />}>
          
            {isVideoPath ? (
              // On /video path: render once, no orientation gating so it won't swap/unmount during rotate
              <MainAppContent />
            ) : (
              // Other routes: enforce orientation and show rotate message via Orientation components
              // <DeviceOrientation
              //   lockOrientation={isMobileOrTablet ? 'portrait' : 'landscape'}
              // >
              //   <Orientation
              //     orientation={isMobileOrTablet ? 'portrait' : 'landscape'}
              //     alwaysRender={false}
              //   >
              //     <MainAppContent />
              //   </Orientation>

              //   <Orientation
              //     orientation={isMobileOrTablet ? 'landscape' : 'default'}
              //     alwaysRender={false}
              //   >
              //     <Box
              //       sx={{
              //         position: 'fixed',
              //         inset: 0,
              //         display: 'flex',
              //         justifyContent: 'center',
              //         alignItems: 'center',
              //         backgroundColor: 'rgba(255,255,255,0.95)',
              //         zIndex: 2000,
              //       }}
              //     >
              //       <Typography variant="h3">
              //         Please rotate your phone to portrait mode, thanks!
              //       </Typography>
              //     </Box>
              //   </Orientation>
              // </DeviceOrientation>
              <>
              {/* Show overlay ONLY when on mobile/tablet and currently landscape */}
              {/* {isMobile && isLandscape   */}
              {isMobileWidescreen 
              ? (
                <Box
                  sx={{
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    zIndex: 2000,
                  }}
                >
                  <Typography variant="h3">
                    Please rotate your device to portrait mode, thanks!
                  </Typography>
                </Box>
              )
            : <MainAppContent />}
            </>
            )}

          </NotistackProvider>
        </RtlLayout>
      </ThemePrimaryColor>
    </ThemeConfig>
  );
}
